import React, { createContext, useContext, useState } from "react";

import {
  getBusinessAccounts,
  getBusinessDetails,
  getBusinessTransactionLimitSettings,
} from "api/business-user-management";
import {
  IBusinessAccountsData,
  IBusinessDetails,
  IBusinessTransactionLimitSettings,
  IGetBusinessTransactionLimitSettingsPayload,
} from "api/business-user-management/types";
import { START_INDEX, ZERO } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";

import {
  IBusinessesContextProps,
  IBusinessesProviderProps,
  IGetBusinessAccountsPayload,
} from "./types";

const DEFAULT_BAL = 0;
const DEFAULT_BUSINESS_ACCOUNTS_DATA: IBusinessAccountsData = {
  totalBalance: DEFAULT_BAL,
  businessAccount: [],
};

export const DEFAULT_BUSINESS_TRANSACTION_LIMIT_SETTINGS: IBusinessTransactionLimitSettings =
  {
    id: "",
    employerId: "",
    singleTransferLimit: ZERO,
    dailyTransferLimit: ZERO,
    reachInAppMax: false,
    exceedInAppMax: false,
    createdAt: "",
    updatedAt: "",
    limitApprovalRequest: [],
  };

export const DEFAULT_BUSINESS_DETAILS: IBusinessDetails = {
  account: {
    accountName: "",
    accountNumber: "",
  },
  business: {
    id: "",
    email: "",
    profileImage: null,
    status: "pending",
    isCompliant: false,
    createdAt: "",
    isActive: false,
    lastLogin: null,
    phoneNumber: "",
    approvedBy: "",
    walletId: "",
    firstName: "",
    lastName: "",
    companyData: {
      companyName: "",
      companyType: "",
      businessLogo: null,
      businessName: "",
      rcNumber: null,
      industry: "",
      companyDescription: "",
      registrationType: "",
      registrationNumber: "",
      address: "",
      country: "",
      state: "",
      lga: null,
      kybSmileJobId: null,
      dob: "",
      bvn: "",
      nin: "",
      bvnReference: null,
      bvnJobId: null,
      bvnJobStatus: null,
      complianceTrails: [],
      businessDocumentStatus: null,
      businessInformationStatus: null,
      directorsInformationStatus: null,
      businessDirectors: [],
      businessDocuments: [],
      socials: {},
      updatedAt: "",
      kybVerificationResult: {
        rcNumber: "",
        companyName: "",
        state: "",
        companyAddress: "",
        companyStatus: "",
        city: "",
        directors: [],
        companyType: "",
      },
      livenessCheck: "",
      livenessReference: null,
      kybJobStatus: null,
    },
    rejectComment: [],
  },
};

const BusinessesContext = createContext<IBusinessesContextProps>({
  businessesTableHeaderCTAWidth: START_INDEX,
  handleUpdateBusinessesTableHeaderCTAWidth: () => {
    return null;
  },
  businessDetails: null,
  setBusinessDetails: () => {
    return null;
  },
  isLoadingBusinessDetails: false,
  setIsLoadingBusinessDetails: () => {
    return null;
  },
  handleGetBusinessDetails: async (_: string) => {
    return {};
  },
  businessAccountsData: DEFAULT_BUSINESS_ACCOUNTS_DATA,
  isLoadingBusinessAccounts: false,
  handleGetBusinessAccounts: async (_: IGetBusinessAccountsPayload) => {
    // do nothing
  },
  isLoadingBusinessTransactionLimitSettings: false,
  businessTransactionLimitSettings: DEFAULT_BUSINESS_TRANSACTION_LIMIT_SETTINGS,
  handleGetBusinessTransactionLimitSettings: async (
    _: IGetBusinessTransactionLimitSettingsPayload
  ) => {
    // do nothing
  },
});

export const BusinessesProvider = (props: IBusinessesProviderProps) => {
  const { children } = props;

  const [businessesTableHeaderCTAWidth, setBusinessesTableHeaderCTAWidth] =
    useState<number>(START_INDEX);
  const [businessDetails, setBusinessDetails] =
    useState<IBusinessDetails | null>(null);
  const [isLoadingBusinessDetails, setIsLoadingBusinessDetails] =
    useState(false);
  const [isLoadingBusinessAccounts, setIsLoadingBusinessAccounts] =
    useState(false);
  const [businessAccountsData, setBusinessAccountsData] =
    useState<IBusinessAccountsData>(DEFAULT_BUSINESS_ACCOUNTS_DATA);
  const [
    isLoadingBusinessTransactionLimitSettings,
    setIsLoadingBusinessTransactionLimitSettings,
  ] = useState(false);
  const [
    businessTransactionLimitSettings,
    setBusinessTransactionLimitSettings,
  ] = useState<IBusinessTransactionLimitSettings>(
    DEFAULT_BUSINESS_TRANSACTION_LIMIT_SETTINGS
  );

  const handleUpdateBusinessesTableHeaderCTAWidth = (value: number) => {
    setBusinessesTableHeaderCTAWidth(value);
  };

  const handleGetBusinessDetails = async (businessId: string) => {
    setIsLoadingBusinessDetails(true);
    let error;
    let data;
    let message;
    try {
      const {
        error: apiError,
        data: apiData,
        message: apiMessage,
      } = await getBusinessDetails({
        businessId,
      });
      if (apiError) {
        customErrorToast({
          message: apiMessage,
        });
      } else {
        setBusinessDetails(apiData);
      }
      error = apiError;
      message = apiMessage;
      data = apiData;
    } catch (err) {
      console.error("Error fetching business details:", err);
    }
    setIsLoadingBusinessDetails(false);
    return { error, data, message };
  };

  const handleGetBusinessAccounts = async (
    payload: IGetBusinessAccountsPayload
  ) => {
    const { businessId, isRefetching } = payload;
    let id = "";
    if (businessId && businessId !== "") {
      id = businessId;
    } else if (businessDetails) {
      id = businessDetails?.business?.id;
    } else {
      console.error("Business absent!");
      return;
    }

    if (!isRefetching) {
      setIsLoadingBusinessAccounts(true);
    }
    try {
      const { error, message, data } = await getBusinessAccounts({
        ...payload,
        businessId: id,
      });
      if (error) {
        customErrorToast({ message });
      } else {
        setBusinessAccountsData(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    if (!isRefetching) {
      setIsLoadingBusinessAccounts(false);
    }
  };

  const handleGetBusinessTransactionLimitSettings = async (
    payload: IGetBusinessTransactionLimitSettingsPayload
  ) => {
    setIsLoadingBusinessTransactionLimitSettings(true);

    try {
      const { error, message, data } =
        await getBusinessTransactionLimitSettings(payload);
      if (error) {
        customErrorToast({ message });
      } else if (data) {
        setBusinessTransactionLimitSettings(data);
      } /*  else {
        customErrorToast({ message: "No data returned" });
      } */
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingBusinessTransactionLimitSettings(false);
  };

  return (
    <BusinessesContext.Provider
      value={{
        businessesTableHeaderCTAWidth,
        handleUpdateBusinessesTableHeaderCTAWidth,
        businessDetails,
        setBusinessDetails,
        isLoadingBusinessDetails,
        setIsLoadingBusinessDetails,
        handleGetBusinessDetails,
        businessAccountsData,
        isLoadingBusinessAccounts,
        handleGetBusinessAccounts,
        isLoadingBusinessTransactionLimitSettings,
        businessTransactionLimitSettings,
        handleGetBusinessTransactionLimitSettings,
      }}
    >
      {children}
    </BusinessesContext.Provider>
  );
};

export const useBusinesses = () => {
  const context = useContext(BusinessesContext);
  if (context === undefined) {
    throw new Error("useBusinesses must be used within a BusinessesProvider");
  }
  return context;
};
